import React, { useState, useEffect } from "react";
import { RiLoaderLine, RiAddLine } from "react-icons/ri";
import {
  createAiApp,
  uploadAiAppCoverImage,
  getAllAiAppCategories,
} from "@api/seo/aiApp";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Modal,
} from "antd";
import { withRouter } from "react-router";
import { SuccessNotification, ErrorNotification } from "../..";
import ReactMarkdown from "react-markdown";

const initialState = {
  title: "",
  coverImage: "",
  logo: "",
  description: "",
  content: "",
  category: "",
  publishedDate: "",
  link: "",
  images: [],
};

const AiAppCreateForm = ({ history }) => {
  const [values, setValues] = useState(initialState);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    fetchCategoriesTags();
  }, []);

  // Handle fetch categories
  const fetchCategoriesTags = async () => {
    setFetchLoading(true);
    const cats = await getAllAiAppCategories();
    setCategories(cats.data);
    setFetchLoading(false);
  };

  // Handle form changes
  const handleFormChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submit
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Validate the form
    if (
      !values.title ||
      !values.description ||
      !values.category ||
      !values.publishedDate ||
      !values.content ||
      !values.link ||
      !values.logo
    ) {
      return ErrorNotification("Please fill all the required fields");
    }

    setLoading(true);
    createAiApp(values)
      .then(() => {
        setLoading(false);
        SuccessNotification(`AI App created successfully`);
        setValues(initialState);
        history.goBack();
      })
      .catch((err) => {
        setLoading(false);
        ErrorNotification(err.response.data.message);
        console.log("CREATE_AI_APP_ERROR", err.response.data.message);
      });
  };

  // Before image upload
  const beforeImageUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle image upload (for both cover image and logo)
  const handleUploadImage = async (options, imageType) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    // Make request to upload image
    try {
      setLoading(true);
      const uploadFunction =
        imageType === "logo" ? uploadAiAppCoverImage : uploadAiAppCoverImage;
      const res = await uploadFunction(fmData, config);
      setValues({
        ...values,
        [imageType]: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const uploadButton = (
    <div>
      {loading ? (
        <RiLoaderLine
          className="remix-icon-loading remix-icon-spin remix-icon"
          size={24}
        />
      ) : (
        <RiAddLine size={24} />
      )}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Row>
      <Col
        span={24}
        style={{
          overflowY: "auto",
          height: "calc(100vh - 100px)",
        }}
      >
        {!fetchLoading && (
          <Form
            labelCol={{
              span: 4,
            }}
            wrapperCol={{
              span: 18,
            }}
          >
            {/* AI App Title */}
            <Form.Item label="Title" required>
              <Input
                name="title"
                placeholder="Enter title"
                onChange={handleFormChange}
                value={values.title}
              />
            </Form.Item>

            {/* AI App Logo */}
            <Form.Item label="Logo" required>
              <Upload
                name="logo"
                listType="picture-card"
                customRequest={(options) => handleUploadImage(options, "logo")}
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeImageUpload}
                multiple={false}
                accept="image/*"
              >
                {values.logo ? (
                  <img src={values.logo} alt="logo" style={{ width: "100%" }} />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* AI App cover image */}
            <Form.Item label="Cover Image" required>
              <Upload
                name="coverImage"
                listType="picture-card"
                customRequest={(options) =>
                  handleUploadImage(options, "coverImage")
                }
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeImageUpload}
                multiple={false}
                accept="image/*"
              >
                {values.coverImage ? (
                  <img
                    src={values.coverImage}
                    alt="cover"
                    style={{ width: "100%" }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* SEO meta description */}
            <Form.Item label="Meta Description" required>
              <Input
                name="description"
                placeholder="Enter meta description"
                onChange={handleFormChange}
                value={values.description}
              />
            </Form.Item>

            {/* Product Link */}
            <Form.Item label="Product Link" required>
              <Input
                name="link"
                placeholder="Enter product link"
                onChange={handleFormChange}
                value={values.link}
              />
            </Form.Item>

            {/* Select drop down with category */}
            <Form.Item label="Category" required>
              <Select
                name="category"
                placeholder="Select a category"
                onChange={(value) => setValues({ ...values, category: value })}
                value={values.category}
              >
                {categories.map((category) => (
                  <Select.Option key={category._id} value={category._id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Publish date */}
            <Form.Item label="Published Date" required>
              <DatePicker
                className="hp-mb-16 hp-mr-16"
                onChange={(date, dateString) =>
                  setValues({ ...values, publishedDate: dateString })
                }
              />
            </Form.Item>

            {/* AI App Content */}
            {!loading && (
              <Form.Item label="Content" required>
                <Col
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div style={{ marginBottom: "16px" }}>
                    <Button
                      onClick={() => setPreviewVisible(true)}
                      style={{ marginRight: "16px" }}
                    >
                      Preview
                    </Button>
                  </div>
                  <Input.TextArea
                    name="content"
                    placeholder="Enter content in markdown format"
                    onChange={handleFormChange}
                    value={values.content}
                    rows={16}
                  />
                </Col>
              </Form.Item>
            )}

            {/* Submit button */}
            <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
              <Button
                type="primary"
                onClick={handleFormSubmit}
                loading={loading}
              >
                Create
              </Button>
            </Form.Item>
          </Form>
        )}
      </Col>

      <Modal
        title="Preview"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <ReactMarkdown>{values.content}</ReactMarkdown>
      </Modal>
    </Row>
  );
};

export default withRouter(AiAppCreateForm);
